// leaseTerms.js — v1.0.0 Phase 3 (Lease-Leverage Module)
//
// Thin data-access layer for the lease_terms API. Mirrors the
// {data, error} shape used by leads.js — every method just wraps
// fetch + Bearer JWT and normalizes errors.
//
// One lease_terms row per lead (1:1 via UNIQUE FK). The companion
// lease_scenarios + improvements layers each get their own libs
// so consumers can import only what they need.
import { supabase } from './supabaseClient';
import { API_BASE_URL } from '../services/api';

const NOT_SIGNED_IN = {
  data: null,
  error: { message: 'Not signed in. Please sign in and try again.' },
};

const NOT_CONFIGURED = {
  data: null,
  error: {
    message:
      'Supabase is not configured. Fill in frontend/.env (REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY) and restart the dev server.',
  },
};

async function callApi(path, init = {}) {
  if (!supabase) return NOT_CONFIGURED;

  const { data: sessionData } = await supabase.auth.getSession();
  const accessToken = sessionData?.session?.access_token;
  if (!accessToken) return NOT_SIGNED_IN;

  try {
    const response = await fetch(`${API_BASE_URL}${path}`, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
        ...(init.headers || {}),
      },
    });
    const payload = await response.json().catch(() => null);
    if (!response.ok || payload?.success === false) {
      return {
        data: null,
        error: {
          message: payload?.error || `Request failed (HTTP ${response.status}).`,
          status: response.status,
          payload,
        },
      };
    }
    return { data: payload, error: null };
  } catch (err) {
    return { data: null, error: { message: err?.message || 'Network error.' } };
  }
}

/**
 * Create a lease_terms row for a lead.
 * Returns 409 if a row already exists for that lead.
 * Returns { data: { lease_terms }, error }.
 */
export async function createLeaseTerms(input) {
  return callApi('/create-lease-terms', {
    method: 'POST',
    body: JSON.stringify(input || {}),
  });
}

/**
 * Fetch the lease_terms row for a lead (or null if none exists).
 * Returns { data: { lease_terms: <row | null> }, error }.
 */
export async function getLeaseTerms(leadId) {
  return callApi(`/get-lease-terms?lead_id=${encodeURIComponent(leadId)}`, {
    method: 'GET',
  });
}

/**
 * PATCH a lease_terms row. Only whitelisted fields are accepted server-side.
 * Returns { data: { lease_terms }, error }.
 */
export async function updateLeaseTerms(patch) {
  return callApi('/update-lease-terms', {
    method: 'PATCH',
    body: JSON.stringify(patch || {}),
  });
}

/**
 * Delete a lease_terms row by id (cascade clears its 3 lease_scenarios).
 * Returns { data: { success: true }, error }.
 */
export async function deleteLeaseTerms(id) {
  return callApi('/delete-lease-terms', {
    method: 'DELETE',
    body: JSON.stringify({ id }),
  });
}
